/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { Link } from 'react-router';

import { snakeCase } from 'lodash';

import { Tag, breakpoints } from '@coursera/cds-core';

import { TrackedReactLink } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/admin-dashboard';

const styles = {
  item: css`
    height: 100%;
    padding: 4px 30px 0 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--cds-spacing-100);
    border-bottom-width: 4px;
    border-bottom-style: solid;
    border-bottom-color: transparent;
    text-decoration: none !important;

    &:hover {
      border-bottom-width: 4px;
      border-bottom-style: solid;
      border-bottom-color: var(--cds-color-interactive-primary);
    }

    &:empty {
      display: none;
    }

    ${breakpoints.down('md')} {
      height: auto;
      padding: var(--cds-spacing-150) 20px;
      justify-content: flex-start;
      border-bottom: none;
    }
  `,
  label: css`
    color: var(--cds-color-grey-600);
    font-size: 0.875rem;

    *:hover > & {
      color: var(--cds-color-grey-975) !important;
    }
  `,
  active: css`
    color: var(--cds-color-blue-600) !important;
    font-weight: bold;

    :hover {
      color: var(--cds-color-blue-600);
    }
  `,
  iconWrapper: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--cds-spacing-50);
    border-radius: var(--cds-border-radius-100);
  `,
  iconWrapperActive: css`
    background-color: var(--cds-color-emphasis-primary-background-xweak);
  `,
  srOnly: css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `,
};

type Props = {
  text: string;
  tag: React.ElementType;
  htmlAttributes: { href: string; 'data-e2e': string; 'aria-label'?: string };
  onClick?: AnchorOnClick | LinkOnClick | ButtonOnClick;
  buttonType?: 'button' | 'submit' | 'reset';
  isActive?: boolean;
  thirdPartyOrganizationId?: string;
  isBetaFeature?: boolean;
  isNewFeature?: boolean;
  icon?: React.ReactNode;
};

type AnchorOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
type LinkOnClick = (event: React.MouseEvent<Link, MouseEvent>) => void;
type ButtonOnClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

const MenuItem: React.FC<Props> = ({
  text,
  isActive,
  isBetaFeature,
  isNewFeature,
  icon,
  htmlAttributes,
  tag = 'button',
  buttonType = 'button',
  thirdPartyOrganizationId,
  onClick,
}) => {
  const children = (
    <>
      {icon && <span css={[styles.iconWrapper, isActive && styles.iconWrapperActive]}>{icon}</span>}
      <span css={[styles.label, isActive && styles.active, !text.trim() && styles.srOnly]}>
        {text}
        {isBetaFeature && (
          <span
            css={css`
              background-color: var(--cds-color-darken-700);
              font-weight: bold;
              color: var(--cds-color-neutral-primary-invert);
              border-radius: 10px;
              padding: 2px var(--cds-spacing-100);
              margin-left: var(--cds-spacing-100);
            `}
          >
            {_t('BETA')}
          </span>
        )}
      </span>
      {isNewFeature && (
        <Tag variant="status" priority="primary" color="highlight1">
          {_t('New')}
        </Tag>
      )}
    </>
  );
  switch (tag) {
    case 'a': {
      const handleClick = onClick as AnchorOnClick;
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <a css={styles.item} onClick={handleClick} {...htmlAttributes}>
          {children}
        </a>
      );
    }
    case 'link': {
      const { href, ...rest } = htmlAttributes;
      return (
        <TrackedReactLink
          trackingName={snakeCase(text)}
          data={{ thirdPartyOrganizationId }}
          css={styles.item}
          href={href}
          {...rest}
        >
          {children}
        </TrackedReactLink>
      );
    }
    case 'button': {
      const handleClick = onClick as ButtonOnClick;

      return (
        <button
          className="button-link text-decoration-none"
          onClick={handleClick}
          {...htmlAttributes}
          css={styles.item}
          // eslint-disable-next-line react/button-has-type
          type={buttonType}
        >
          {children}
        </button>
      );
    }
    default:
      return <span className="text-decoration-none inline-block">{children}</span>;
  }
};

export default MenuItem;
